import React, { useEffect, useState } from "react";
import "./util/global.ts";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import {
  selectIsAuthenticated,
  selectLoading,
  loginSuccess,
  setLoading,
  selectUser,
} from "./store/slices/authSlice";
import DefaultLayout from "./layout/DefaultLayout";
import DashboardIndex from "./page/Dashboard";
import Login from "./components/Login/Login";
import PublicRoute from "./components/Route/PublicRoute";
import ProtectedRoute from "./components/Route/ProtectedRoute";
import AddLanguage from "./page/Language/Components/AddLanguage/AddLanguage";
import AddBanner from "./page/Banner/Components/AddBanner/AddBanner";
import AddUser from "./page/Users/Components/AddUser/AddUser";
import BannerIndex from "./page/Banner";
import LanguageIndex from "./page/Language";
import UserIndex from "./page/Users";
import EbookIndex from "./page/Ebook";
import EPaperIndex from "./page/EPaper";
import AddEPaper from "./page/EPaper/Components/AddEPaper/AddEPaper";
import AddEbook from "./page/Ebook/Components/AddEbook/AddEbook";
import TestSeriesIndex from "./page/TestSeries";
import SubjectIndex from "./page/Subject";
import AddSubject from "./page/Subject/Components/AddSubject/AddSubject";
import AddTestSeries from "./page/TestSeries/Components/AddTestSeries/AddTestSeries";
import AddStream from "./page/Stream/Components/AddStream/AddStream";
import StreamIndex from "./page/Stream";
import AddEducation from "./page/Education/Components/AddEducation/AddEducation";
import EducationIndex from "./page/Education";
import AddTestQuestion from "./page/TestQuestion/Components/AddTestQuestion/AddTestQuestion";
import TestQuestionIndex from "./page/TestQuestion";
import AddExcel from "./page/TestQuestion/Components/AddExcel/AddExcel";
import NotificationForm from "./page/Notifications/Notification";
import ApiService from "./service/ApiService";
import { apiPaths } from "./service/apiPaths";
import Loader from "./components/Loader";
import TestQuestion from "./page/TestQuestion/Components/TestQuestion";
import PrivacyPolicy from "./page/PrivacyPolicy/PrivacyPolicy";
import AboutUs from "./page/AboutUs/AboutUs";
import Help from "./page/Help/Help";
import Transactions from "./page/Transaction/Components/Transactions";
import CreateArticle from "./page/News/CreateArtcile/CreateArticle";
import Category from "./page/Category/Category";
import AddCategory from "./page/Category/Components/AddCategory/AddCategory";
import Articles from "./page/News/Articles";
import EditArticle from "./page/News/EditArticle/EditArticle";
import LiveMcq from "./page/LiveMcq/Components/LiveMcq";
import AddLiveMcqExcel from "./page/LiveMcq/Components/AddLiveMcqExcel/AddLiveMcqExcel";
import AddLiveQuestion from "./page/LiveMcq/Components/AddLiveQuestion/AddLiveQuestion";
import Coupon from "./page/Coupons/Coupon";
import AddCoupon from "./page/Coupons/Components/AddCoupon/AddCoupon";
import EditCoupon from "./page/Coupons/Components/EditCoupon/EditCoupon";
import AddCouponCategory from "./page/CouponCategory/AddCategory/AddCategory";
import CouponCategory from "./page/CouponCategory/CouponCategory";
import EditCouponCategoryIndex from "./page/CouponCategory/EditCategory/EditCouponCategory";
import MasterUsers from "./page/MasterUser/MasterUsers";
import CronSettingsIndex from "./page/CronSettings";
import TestSubmissionAnalysis from "./page/TestSubmissionAnalysis";

// Main App Content with Redux integration
const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading = useAppSelector(selectLoading);
  const user = useAppSelector(selectUser);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const verifyToken = async () => {
    dispatch(setLoading(true));
    try {
      const res = await ApiService({
        method: "GET",
        endpoint: apiPaths.verifyToken,
      });

      if (res.success && res.user) {
        dispatch(loginSuccess({ user: res.user }));
        console.log("Token verified, user loaded:", res.user);
      } else {
        throw new Error("Invalid token response");
      }
    } catch (error) {
      console.log("Error in token verification:", error);
      setTokenError("Token verification failed");
      // Clear any stored token
      // localStorage.removeItem('admin_authToken');
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    verifyToken();
  }, [dispatch]);

  if (loading || (!user && !tokenError)) {
    return <Loader />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {!loading && !isAuthenticated && (
          <Route
            path="/login"
            element={
              <PublicRoute element={<Login />} isLoggedIn={isAuthenticated} />
            }
          />
        )}

        {/* Dashboard Route*/}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <DashboardIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Language Routes*/}
        <Route
          path="/language"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <LanguageIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-language"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddLanguage />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Education Routes*/}
        <Route
          path="/education"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <EducationIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-education"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddEducation />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Stream Routes*/}
        <Route
          path="/stream"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <StreamIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-stream"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddStream />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* User Management Routes*/}
        <Route
          path="/user"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <UserIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-user"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddUser />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/transaction"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <Transactions />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/send-notification"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <NotificationForm />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/master-users"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <MasterUsers />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Banner Routes*/}
        <Route
          path="/banner"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <BannerIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-banner"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddBanner />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Ebook Routes*/}
        <Route
          path="/ebook"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <EbookIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* E-paper Routes*/}
        <Route
          path="/e-paper"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <EPaperIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-e-paper"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddEPaper />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-e-paper/:id"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddEPaper />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-ebook"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddEbook />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Test Series Routes*/}
        <Route
          path="/test-series"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <TestSeriesIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-test-series"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddTestSeries />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Question Routes*/}
        <Route
          path="/questions"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <TestQuestion />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-excel"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddExcel />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-question"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <TestQuestionIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-test-question"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddTestQuestion />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Live MCQ Routes*/}
        <Route
          path="/add-live-mcq-excel"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddLiveMcqExcel />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-live-mcq"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddLiveQuestion />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Subject Routes*/}
        <Route
          path="/subject"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <SubjectIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-subject"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddSubject />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Settings Routes*/}
        <Route
          path="/privacy-policy"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <PrivacyPolicy />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/about-us"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AboutUs />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <Help />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cron-settings"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <CronSettingsIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* News & Articles Routes*/}
        <Route
          path="/news-blogs"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <Articles />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/category"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <Category />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-article"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <CreateArticle />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-category"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddCategory />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Coupon Routes*/}
        <Route
          path="/coupon"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <Coupon />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-coupon"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddCoupon />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-coupon/:id"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <EditCoupon />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-coupon-category"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <AddCouponCategory />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/coupon-category"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <CouponCategory />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-coupon-category/:couponCategoryId"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <EditCouponCategoryIndex />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Test Submission Analysis Route */}
        <Route
          path="/test-submission-analysis"
          element={
            <ProtectedRoute>
              <DefaultLayout>
                <TestSubmissionAnalysis />
              </DefaultLayout>
            </ProtectedRoute>
          }
        />

        {/* Fallback route */}
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />}
        />
      </Routes>
    </BrowserRouter>
  );
};

// Main App component with Redux Provider
function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
